<template>
  <section class="address-section">
    <div
      v-if="showCurrentDetails"
      class="current-address-section"
    >
      <div class="section-title">
        <h2>Address Details</h2>
      </div>
      <b-container>
        <b-row>
          <b-col cols="12">
            <div class="section-card">
              <div class="billing-details">
                <h3>Billing Address</h3>
                <div class="addressee">
                  {{ addressCompact.billingName }}
                </div>
                <div class="address">
                  {{ addressCompact.billingAddress }}
                </div>
              </div>
              <div class="shipping-details">
                <h3>Shipping Address</h3>
                <template v-if="!pickup">
                  <div class="addressee">
                    {{ addressCompact.shippingName }}
                  </div>
                  <div class="address">
                    {{ addressCompact.shippingAddress }}
                  </div>
                </template>
                <template v-else>
                  <span><strong>Order is pickup</strong></span>
                </template>
              </div>
              <div
                v-if="!disabled"
                class="buttons"
              >
                <div class="edit-button">
                  <b-btn
                    variant="outline-primary"
                    @click="openForm"
                  >
                    Change
                  </b-btn>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
    <div v-if="allowPickup && showPickupForm">
      <div class="section-title">
        <h2>Shipping or Pickup?</h2>
      </div>
      <b-container>
        <b-row>
          <b-col cols="12">
            <div class="section-card">
              <div
                class="pickup"
              >
                <div class="label">
                  Will you pickup your wines?
                </div>
                <swoggle
                  :value="pickup"
                  label-on="Yes"
                  label-off="No"
                  @input="(val) => $emit('update:pickup', val)"
                />
              </div>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
    <div v-if="showAddressForm">
      <div
        class="section-title"
        style="margin-top:100px;"
      >
        <h2>Address Details</h2>
      </div>
      <b-container>
        <b-row>
          <b-col cols="12">
            <div class="section-card">
              <address-block
                v-model="returnedData"
                :saved-address-list="savedAddressList"
                :countries="acceptedCountries"
                :provinces="provincesList"
                :use-google="useGoogleAddress"
                :google-api-key="googleApiKey"
                :email-option="true"
                :mode="addressCaptureMode"
                :edit-form="editAddress"
              />
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
    <div v-if="!pickup">
      <div
        class="section-title"
        style="margin-top:100px;"
      >
        <h2>Authority To Leave</h2>
      </div>
      <b-container>
        <b-row>
          <b-col cols="12">
            <authority-to-leave />
          </b-col>
        </b-row>
      </b-container>
    </div>
  </section>
</template>

<script>
import { mapMutations, mapGetters, mapState } from 'vuex'
import AuthorityToLeave from './AuthorityToLeave'

export default {
  name: 'address-section',
  components: {
    'authority-to-leave': AuthorityToLeave
  },
  props: {
    orderNum: {
      type: String,
      required: false,
      default: null
    },
    currentShipping: {
      type: Object,
      required: true
    },
    currentBilling: {
      type: Object,
      required: true
    },
    pickup: {
      type: Boolean,
      required: true
    },
    shippingSameAsBilling: {
      type: Boolean,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      notRequiredFields: {
        shipping: ['selectedaddress', 'business', 'street2', 'deliveryInstructions'],
        billing: ['selectedaddress', 'business', 'street2', 'deliveryInstructions', 'tax_number']
      },
      addressCaptureMode: 'bs',
      showAddressForm: true,
      showPickupForm: false,
      showCurrentDetails: false,
      editAddress: false
    }
  },
  computed: {
    ...mapGetters('somnambulistui', ['getSelectedQuantityObject']),
    ...mapState('somnambulistui', ['shippingAddressValid', 'billingAddressValid']),
    // ...mapState('somnambulistui', ['orderId', 'shippingAddress', 'billingAddress', 'shippingSameAsBilling']),
    defaultMessage: function() {
      return this.$store.getters['ecommerce/getConfigItem']('authority_to_leave', 'default') || 'Authority to leave the package in a safe place if you are not home.'
    },
    enabledMessage: function() {
      return this.$store.getters['ecommerce/getConfigItem']('authority_to_leave', 'enabled_msg') || null
    },
    disabledMessage: function() {
      return this.$store.getters['ecommerce/getConfigItem']('authority_to_leave', 'disabled_msg') || null
    },

    allowPickup() {
      return this.getSelectedQuantityObject.allowPickup
    },
    savedAddressList() {
      return Object.values(this.$store.getters['ecommerce/addresses'])
    },
    googleApiKey: function() {
      return this.$store.state.somnambulistui.googleAddressesAPIKey
    },
    useGoogleAddress: function() {
      return !!this.googleApiKey
    },
    addressCompact() {
      return {
        billingName: [
          this.currentBilling.firstname,
          this.currentBilling.lastname
        ].filter(field => field && field.length).join(' '),
        billingAddress: [
          this.currentBilling.business,
          this.currentBilling.street1,
          this.currentBilling.street2,
          this.currentBilling.city,
          this.currentBilling.state,
          this.currentBilling.zip || this.currentBilling.postcode,
          this.currentBilling.country === '' ? this.currentBilling.country.toUpperCase() : ''
        ].filter(field => field && field.length).join(', '),
        shippingName: [
          this.currentShipping.firstname,
          this.currentShipping.lastname
        ].filter(field => field && field.length).join(' '),
        shippingAddress: [
          this.currentShipping.business,
          this.currentShipping.street1,
          this.currentShipping.street2,
          this.currentShipping.city,
          this.currentShipping.state,
          this.currentShipping.zip || this.currentShipping.postcode,
          this.currentShipping.country === '' ? this.currentShipping.country.toUpperCase() : ''
        ].filter(field => field && field.length).join(', ')
      }
    },
    acceptedCountries() {
      const countryData = this.$store.getters['somnambulistui/getAllowedCountries']
      const allowedCountries = []
      Object.entries(countryData).forEach(([key, value]) => {
        const out = { name: key, display: value }
        allowedCountries.push(out)
      })
      return allowedCountries
    },
    provincesList() {
      const provData = this.$store.getters['ecommerce/getRegions']
      let formatProv = {}
      Object.entries(provData).forEach(([key, value]) => {
        let provArr = []
        Object.entries(value).forEach(([stateKey, stateName]) => {
          provArr.push({ name: stateKey, display: stateName })
        })
        formatProv[key] = provArr
      })
      return formatProv
    },
    returnedData: {
      get() {
        return {
          shipping: {
            street1: this.currentShipping.street1,
            city: this.currentShipping.city,
            zip: this.currentShipping.zip || this.currentShipping.postcode,
            postcode: this.currentShipping.zip || this.currentShipping.postcode,
            country: this.currentShipping.country,
            state: this.currentShipping.state,
            street2: this.currentShipping.street2,
            email: this.currentShipping.email,
            phone: this.currentShipping.phone,
            business: this.currentShipping.business,
            deliveryInstructions: this.currentShipping.deliveryInstructions,
            firstname: this.currentShipping.firstname,
            lastname: this.currentShipping.lastname
          },
          billing: {
            street1: this.currentBilling.street1,
            city: this.currentBilling.city,
            zip: this.currentBilling.zip || this.currentBilling.postcode,
            postcode: this.currentBilling.zip || this.currentBilling.postcode,
            country: this.currentBilling.country,
            state: this.currentBilling.state,
            street2: this.currentBilling.street2,
            business: this.currentBilling.business,
            email: this.currentBilling.email,
            firstname: this.currentBilling.firstname,
            lastname: this.currentBilling.lastname
          },
          useBillingAsShipping: this.shippingSameAsBilling
        }
      },
      set(newValue) {
        this.SET_SHIPPING_ADDRESS(newValue.shipping)
        this.SET_BILLING_ADDRESS(newValue.billing)
        this.SET_SHIPPING_SAME_AS_BILLING(newValue.useBillingAsShipping)
        this.SET_PHONE_NUMBER(newValue.shipping.phone)
      }
    }
  },
  watch: {
    pickup(val) {
      if (val) {
        this.addressCaptureMode = 'b'
      }
      else {
        this.addressCaptureMode = 'bs'
      }
    },
    returnedData: {
      handler(val) {
        this.validateForm(val)
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    // Show Correct Forms on init
    if (this.orderNum !== null && this.billingAddressValid) {
      this.showAddressForm = false
      this.showCurrentDetails = true
    }
    if (this.showAddressForm) { // this.$store.getters['getModuleSetting']('somnambulist', 'pickup')) {
      this.showPickupForm = true
    }
  },
  methods: {
    ...mapMutations('somnambulistui', ['SET_BILLING_ADDRESS', 'SET_SHIPPING_ADDRESS', 'SET_SHIPPING_ADDRESS_VALID', 'SET_BILLING_ADDRESS_VALID', 'SET_PHONE_NUMBER', 'SET_SHIPPING_SAME_AS_BILLING']),
    openForm() {
      this.editAddress = true
      if (this.pickup) {
        this.addressCaptureMode = 'b'
      }
      this.showAddressForm = true
      this.showPickupForm = true
      this.showCurrentDetails = false
    },
    validateForm(formVal) {
      let shipErrors = []
      let billErrors = []
      const disallowedValues = ['', null, 'undefined']
      const disallowedTypes = ['undefined']

      Object.entries(formVal.billing).map((bvalue) => {
        if ((disallowedValues.includes(bvalue[1]) || disallowedTypes.includes(typeof bvalue[1])) && !this.notRequiredFields.billing.includes(bvalue[0])) {
          billErrors.push(`Billing - ${bvalue[0]}: Field is required`)
        }
      })

      if (!formVal.useBillingAsShipping) {
        Object.entries(formVal.shipping).map((value) => {
          if ((disallowedValues.includes(value[1]) || disallowedTypes.includes(typeof value[1])) && !this.notRequiredFields.shipping.includes(value[0])) {
            shipErrors.push(`Shipping - ${value[0]}: Field is required`)
          }
        })
      }
      else {
        if (formVal.shipping.phone === '' || typeof formVal.shipping.phone === 'undefined') {
          billErrors.push('Shipping Notes - Phone: Field is required')
        }
      }

      if (shipErrors.length > 0) {
        this.SET_SHIPPING_ADDRESS_VALID(false)
        return false
      }

      if (billErrors.length > 0) {
        this.SET_BILLING_ADDRESS_VALID(false)
        return false
      }

      if (billErrors.length === 0 && shipErrors.length === 0) {
        this.SET_SHIPPING_ADDRESS_VALID(true)
        this.SET_BILLING_ADDRESS_VALID(true)
        return true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@scss/custom.scss';

  .address-section {
    margin: 6em 0 0 0;
    .section-title {
      margin: 0 0 2em 0;
      width: 100%;
      text-align: center;
    }
    .pickup, .same-as-billing {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      background-color: var(--main-card-background-color);
      padding: 0 0 2em 0;
      @include media-breakpoint-down('sm') {
        padding: 0 0 1em 0;
      }
      text-align: center;
      .label {
        margin-bottom: .5em;
      }
    }
    .same-as-billing {
      border-bottom: 1px solid var(--main-border-color);
      border-top: 1px solid var(--main-border-color);
      padding: 2em 0;
      @include media-breakpoint-down('sm') {
        padding: 1em 0;
      }
    }
    .address-details-container {
      padding: 2em 0;
      @include media-breakpoint-down('sm') {
        padding: 1em 0;
      }
    }
    .add-address {
      padding: 2em 0;
      @include media-breakpoint-down('sm') {
        padding: 1em 0;
      }
    }
    .delivery-notes {
      textarea {
        // &:placeholder-shown {
        //   text-align: center;
        // }
      }
      background-color: var(--main-card-background-color);
      padding: 2em 0 0 0;
      @include media-breakpoint-down('sm') {
        padding: 1em 0 0 0;
      }
      text-align: center;
      border-top: 1px solid var(--main-border-color);
    }
    .billing-details, .shipping-details {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      flex-grow: 1;
      flex-shrink: 0;
      max-width: 100%;
      h3 {
        padding: 1.2rem 0 0.8rem;
        font-weight: 600;
      }
      .addressee {
        font-weight: 600;
      }
    }
    .buttons {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      flex-grow: 0;
      flex-shrink: 0;
      .edit-button {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: 1em;
        @include media-breakpoint-down('sm') {
          margin: 1em 0 1em 1em;
        }
      }
    }
  }
</style>
